import { api } from '../api';

export type EstadoRenaperValidacion =
  | 'pendiente'
  | 'validado'
  | 'no_coincide'
  | 'no_encontrado'
  | 'error';

export interface RenaperValidacion {
  estado: EstadoRenaperValidacion;
  consultadoAt: string;
  motivo?: string;
  detalle?: {
    matchApellido?: boolean;
    matchNombre?: boolean;
    matchFecha?: boolean;
    codigo?: number;
  };
  proveedor?: string;
}

export interface ResultadoValidacion {
  asociadoId: string;
  estado: EstadoRenaperValidacion;
  motivo?: string;
  detalle?: RenaperValidacion['detalle'];
}

export interface ResumenRenaperPadron {
  padronId: string;
  total: number;
  pendientes: number;
  validados: number;
  noCoincide: number;
  noEncontrado: number;
  errores: number;
}

export type EstadoBatchJob = 'corriendo' | 'terminado' | 'error';

/**
 * Estado de la validación masiva del padrón contra Renaper. El POST de
 * `validarRenaperPadron` ahora dispara el batch en background y devuelve este
 * objeto inmediatamente (HTTP 202). La UI poll `getRenaperBatchJobLatest`
 * hasta que estado pase a `terminado` o `error`.
 */
export interface RenaperBatchJob {
  id: string;
  padronId: string;
  estado: EstadoBatchJob;
  startedAt: string;
  finishedAt?: string;
  candidatos?: number;
  procesados?: number;
  resumen?: {
    total: number;
    pendientes: number;
    validados: number;
    noCoincide: number;
    noEncontrado: number;
    errores: number;
    /**
     * Cantidad de asociados que tuvieron pisos aplicados sobre sus campos
     * durante este batch. Esos quedaron en estado `validado` automáticamente
     * (porque la data ahora matchea con Renaper). El banner ofrece un botón
     * "Ver detalle de pisos" cuando este número es > 0.
     */
    pisosAplicados?: number;
  };
  error?: string;
  disparadoPor?: string;
}

export interface AsociadoConPisos {
  asociadoId: string;
  documento: string;
  nombres: string;
  apellSolt: string;
  legajo: string;
  pisos: Array<{
    campo: string;
    valorAnterior: string;
    valorNuevo: string;
  }>;
}

export interface AsociadosConPisosPage {
  total: number;
  items: AsociadoConPisos[];
}

export async function listarAsociadosConPisos(
  padronId: string,
  paging: { offset?: number; limit?: number } = {},
): Promise<AsociadosConPisosPage> {
  const { data } = await api.get<AsociadosConPisosPage>(
    `/admin/renaper/padrones/${padronId}/pisos`,
    { params: paging },
  );
  return data;
}

export async function validarRenaperAsociado(id: string): Promise<ResultadoValidacion> {
  const { data } = await api.post<ResultadoValidacion>(`/admin/renaper/asociados/${id}/validar`);
  return data;
}

export async function validarRenaperPadron(id: string): Promise<RenaperBatchJob> {
  const { data } = await api.post<RenaperBatchJob>(`/admin/renaper/padrones/${id}/validar`);
  return data;
}

/**
 * Devuelve el último job de validación masiva (cualquier estado) o `null`
 * si nunca corrió. El server devuelve 404 cuando no hay job — lo traducimos
 * a `null` para que el caller no tenga que catch-ear el error.
 */
export async function getRenaperBatchJobLatest(id: string): Promise<RenaperBatchJob | null> {
  try {
    const { data } = await api.get<RenaperBatchJob>(`/admin/renaper/padrones/${id}/jobs/latest`);
    return data;
  } catch (err) {
    const e = err as { response?: { status?: number } };
    if (e.response?.status === 404) return null;
    throw err;
  }
}

export async function getResumenRenaper(id: string): Promise<ResumenRenaperPadron> {
  const { data } = await api.get<ResumenRenaperPadron>(`/admin/renaper/padrones/${id}/resumen`);
  return data;
}
